// Deploy — operations, promoted out of the Strategy Lab into its own top-level
// page (D-080). Pairing / keys / cloud setup stay in Settings → Connections;
// this page is the day-to-day: see your paired server's live status, deploy a
// strategy you built, and start / stop / flatten / switch paper↔live. It reuses
// the same trading-server relay the Connections panel does (D-125).

import { el } from "../lib/dom.js";
import { getStoredLicense } from "../lib/license.js";
import { fetchServerStatus, deployStrategy, deployCalendar, deployNews, deployEarnings, sendControl, setMode, isOnline, agoLabel, type ServerStatus } from "../lib/trading-server.js";
import { strategyStore } from "../lib/strategy.js";
import { loadStrategy, listSavedStrategies, loadNamedStrategy } from "../lib/composable/storage.js";
import type { Strategy } from "../lib/composable/model.js";

// DEV-ONLY preview so the full panel can be seen without a paired server. Toggled
// by a small "Preview with sample data" link on the empty state; remove before
// this ships to users (D-125).
const MOCK_STATUS: ServerStatus = {
  paired: true,
  name: "Raspberry Pi (sample)",
  publicKey: "dev-mock-key",
  lastSeen: Math.floor(Date.now() / 1000) - 4,
  status: {
    strategy: "Gold swing v3",
    account: "PA-1048576",
    mode: "dry-run",
    state: "in-position",
    openPositions: [{ symbol: "GC=F", side: "long", qty: 1, entry: 2412.4, uPnL: 63.2 }],
    pnlToday: 128.5,
    ts: Date.now(),
  },
};

export function renderDeployPage(root: HTMLElement): () => void {
  let disposed = false;
  let mock = false; // dev preview

  const note = (text: string): HTMLElement =>
    el("div", { style: { flex: "1", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", textAlign: "center", padding: "0 24px" } }, [
      el("div", { text, style: { font: "var(--font-body)", color: "var(--text-secondary)" } }),
    ]);

  // Cards live in a centred column so they don't stretch on wide desktop layouts.
  const wrapCards = (nodes: HTMLElement[]): HTMLElement =>
    el("div", { style: { maxWidth: "560px", width: "100%", margin: "0 auto", display: "flex", flexDirection: "column", gap: "10px" } }, nodes);

  // A control button that shows progress, then a ✓/✕ tick, then reverts.
  function controlBtn(label: string, run: () => Promise<boolean>, confirmMsg?: string): HTMLElement {
    const b = el("button", { className: "pill", text: label });
    b.addEventListener("click", () => {
      if (confirmMsg && !window.confirm(confirmMsg)) return;
      const prev = b.textContent ?? label;
      b.textContent = "…";
      (b as HTMLButtonElement).disabled = true;
      void run().then((ok) => {
        (b as HTMLButtonElement).disabled = false;
        b.textContent = ok ? `${prev} ✓` : `${prev} ✕`;
        setTimeout(() => (b.textContent = prev), 1500);
      });
    });
    return b;
  }

  function strategyRow(strat: Strategy, publicKey: string, isDeployed: boolean): HTMLElement {
    const status = el("span", { style: { font: "var(--font-caption)", color: "var(--up)", whiteSpace: "nowrap" }, text: isDeployed ? "Deployed ✓" : "" });
    const btn = el("button", {
      className: "pill",
      text: isDeployed ? "Redeploy" : "Deploy",
      onClick: () => {
        btn.textContent = isDeployed ? "Redeploying…" : "Deploying…";
        (btn as HTMLButtonElement).disabled = true;
        void deployStrategy(publicKey, strat).then((ok) => {
          btn.textContent = isDeployed ? "Redeploy" : "Deploy";
          (btn as HTMLButtonElement).disabled = false;
          status.textContent = ok ? "Sent ✓" : "Failed";
          status.style.color = ok ? "var(--up)" : "var(--down)";
          // Keep the box's econ-calendar (D-065) + news (D-072) + earnings (D-091) feeds current.
          if (ok) { void deployCalendar(publicKey); void deployNews(publicKey); void deployEarnings(publicKey); void refresh(); }
        });
      },
    });
    return el("div", { style: { display: "flex", gap: "6px", alignItems: "center" } }, [
      el("div", { text: strat.name, style: { font: "var(--font-body)", flex: "1", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
      status,
      btn,
    ]);
  }

  // A section card matching the Watch / Setups look.
  const card = (title: string | null, children: HTMLElement[]): HTMLElement =>
    el("div", { style: { border: "1px solid var(--border)", borderRadius: "12px", background: "var(--bg)", padding: "14px 16px", display: "flex", flexDirection: "column", gap: "8px" } },
      [...(title ? [el("div", { text: title, style: { font: "var(--font-header)" } })] : []), ...children]);

  const onlineChip = (online: boolean): HTMLElement =>
    el("span", { style: { display: "inline-flex", alignItems: "center", gap: "5px", flex: "none", font: "var(--font-caption)", color: online ? "var(--up)" : "var(--text-secondary)", background: "var(--bg-fill)", padding: "2px 9px", borderRadius: "999px" } }, [
      el("span", { style: { width: "6px", height: "6px", borderRadius: "50%", flex: "none", background: online ? "var(--up)" : "var(--text-secondary-faint)" } }),
      el("span", { text: online ? "Online" : "Offline" }),
    ]);

  // Key/value row. `tone` colours the value (for P&L).
  const kv = (label: string, value: string, tone?: "up" | "down"): HTMLElement =>
    el("div", { style: { display: "flex", gap: "10px", font: "var(--font-caption)" } }, [
      el("div", { text: label, style: { color: "var(--text-secondary)", width: "104px", flex: "none" } }),
      el("div", { text: value, style: { color: tone === "up" ? "var(--up)" : tone === "down" ? "var(--down)" : "var(--text-primary)", fontWeight: "500" } }),
    ]);

  function panel(status: ServerStatus): HTMLElement[] {
    const online = isOnline(status.lastSeen);
    const s = status.status ?? {};

    // Status card — name + online chip, then a tidy key/value readout.
    const kvs: HTMLElement[] = [];
    if (s.strategy) kvs.push(kv("Strategy", String(s.strategy)));
    if (s.account) kvs.push(kv("Account", String(s.account)));
    if (s.mode) kvs.push(kv("Mode", s.mode === "live" ? "Live" : "Paper (dry-run)"));
    if (s.state) kvs.push(kv("State", String(s.state)));
    const positions = Array.isArray(s.openPositions) ? s.openPositions : [];
    const posCount = Array.isArray(s.openPositions) ? s.openPositions.length : typeof s.openPositions === "number" ? s.openPositions : undefined;
    if (posCount !== undefined) kvs.push(kv("Open positions", String(posCount)));
    for (const p of positions) {
      const upnl = typeof p.uPnL === "number" ? ` (${p.uPnL >= 0 ? "+" : ""}${p.uPnL})` : "";
      kvs.push(kv("", `${p.symbol} ${p.side} ${p.qty} @ ${p.entry}${upnl}`));
    }
    if (typeof s.pnlToday === "number") kvs.push(kv("P&L today", `${s.pnlToday >= 0 ? "+" : ""}${s.pnlToday}`, s.pnlToday >= 0 ? "up" : "down"));
    kvs.push(kv("Heartbeat", agoLabel(status.lastSeen)));

    const statusCard = card(null, [
      el("div", { style: { display: "flex", alignItems: "center", gap: "8px" } }, [
        el("div", { text: status.name ?? "Trading server", style: { font: "var(--font-header)", flex: "1", minWidth: "0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } }),
        onlineChip(online),
      ]),
      el("div", { style: { display: "flex", flexDirection: "column", gap: "4px", marginTop: "2px" } }, kvs),
    ]);

    const cards = [statusCard];

    // Deploy + Controls need the box's public key (strategies stay sealed to it).
    if (status.publicKey) {
      const pubKey = status.publicKey;
      const deployable: Strategy[] = [];
      const seen = new Set<string>();
      for (const name of listSavedStrategies()) {
        const st = loadNamedStrategy(name) as Strategy | null;
        if (st && st.name && !seen.has(st.name)) { deployable.push(st); seen.add(st.name); }
      }
      const working = loadStrategy() as Strategy | null;
      if (working && working.name && !seen.has(working.name)) { deployable.push(working); seen.add(working.name); }
      cards.push(card("Deploy a strategy", deployable.length
        ? deployable.map((strat) => strategyRow(strat, pubKey, s.strategy === strat.name))
        : [el("div", { text: "Build and backtest a strategy in the Strategy Lab (Tune), then deploy it here.", style: { font: "var(--font-caption)", color: "var(--text-secondary)" } })]));

      const live = s.mode === "live";
      cards.push(card("Controls", [
        el("div", { style: { display: "flex", gap: "6px", flexWrap: "wrap", alignItems: "center" } }, [
          controlBtn("Start", () => sendControl("start")),
          controlBtn("Stop", () => sendControl("stop")),
          controlBtn("Flatten", () => sendControl("flatten"), "Close all open positions now and stop?"),
        ]),
        el("div", { text: "Start = take new trades · Stop = pause new entries (open trades stay managed) · Flatten = close everything now.", style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
        el("div", { style: { display: "flex", gap: "6px", alignItems: "center", marginTop: "4px", borderTop: "1px solid var(--border)", paddingTop: "10px" } }, [
          el("div", { text: `Mode: ${live ? "Live" : "Paper (dry-run)"}`, style: { font: "var(--font-caption)", color: live ? "var(--down)" : "var(--text-secondary)", flex: "1" } }),
          live
            ? controlBtn("Switch to paper", () => setMode("dry-run"))
            : controlBtn("Go live", () => setMode("live"), "Request LIVE trading with real money? Your server's safety rails still apply (live only if enabled there)."),
        ]),
        el("div", { text: "“Go live” is a request — your server keeps its own rails (dry-run by default; live only if you've enabled it there). Which account trades is set by the Account ID in Settings → Connections.", style: { font: "var(--font-caption)", color: "var(--text-secondary-faint)" } }),
      ]));
    }
    return cards;
  }

  const container = el("div", { className: "scroll", style: { display: "flex", flexDirection: "column", height: "100%", overflowY: "auto", padding: "16px" } });
  root.replaceChildren(container);

  // DEV: a small link on the empty state that flips into a sample-data preview.
  const devPreviewLink = (): HTMLElement => {
    const b = el("button", { text: "Preview with sample data (dev)", style: { background: "none", border: "0", color: "var(--text-secondary-faint)", font: "var(--font-caption)", cursor: "pointer", marginTop: "8px" } });
    b.addEventListener("click", () => { mock = true; void refresh(); });
    return b;
  };
  const devBanner = (): HTMLElement => {
    const b = el("button", { text: "Sample data (dev) — exit preview", style: { background: "var(--bg-fill)", border: "1px solid var(--border)", borderRadius: "8px", color: "var(--text-secondary)", font: "var(--font-caption)", padding: "5px 10px", cursor: "pointer", alignSelf: "flex-start" } });
    b.addEventListener("click", () => { mock = false; void refresh(); });
    return b;
  };

  async function refresh(): Promise<void> {
    if (disposed) return;
    if (mock) { container.replaceChildren(wrapCards([devBanner(), ...panel(MOCK_STATUS)])); return; }
    const license = await getStoredLicense();
    if (disposed) return;
    if (!license?.token) { const n = note("Sign in from Settings → Connections to deploy a strategy to your trading server."); n.appendChild(devPreviewLink()); container.replaceChildren(n); return; }
    const status = await fetchServerStatus();
    if (disposed) return;
    if (!status?.paired) { const n = note("Pair a trading server in Settings → Connections, then deploy strategies to it here."); n.appendChild(devPreviewLink()); container.replaceChildren(n); return; }
    container.replaceChildren(wrapCards(panel(status)));
  }

  void strategyStore.init().then(() => refresh());
  const poll = setInterval(() => void refresh(), 10_000);
  return () => { disposed = true; clearInterval(poll); };
}
